import { forwardRef } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/cn";

/**
 * Tooltip — Radix asosida.
 * UX:
 *   - delay 200ms (Linear stilida)
 *   - arrow yo'q (sodda)
 *   - keyboard accessible (Radix avto)
 *   - shortcut hint qo'llash mumkin (mono font kbd ichida)
 */
export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // R4: warm soft shadow, 12px radius (rounded-xl)
        "z-[var(--z-popover)] overflow-hidden rounded-xl px-3 py-1.5",
        "bg-[var(--bg-elevated)] border border-[var(--border-subtle)] shadow-[var(--shadow-soft)]",
        "text-[11px] font-medium text-[var(--text-primary)]",
        "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = "TooltipContent";

/**
 * Sodda wrapper — odatiy use case (matn tooltip).
 *
 * <Tooltip content="Linkni nusxalash">
 *   <Button>Copy</Button>
 * </Tooltip>
 */
export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  delayDuration?: number;
  /** Shortcut hint (Cmd+K stilida). content yonida kbd shaklida ko'rsatiladi. */
  shortcut?: string;
}

export function Tooltip({
  content,
  children,
  side = "top",
  delayDuration = 200,
  shortcut,
}: TooltipProps) {
  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>
        <span className="inline-flex items-center gap-2">
          {content}
          {shortcut && (
            <kbd className="font-mono text-[10px] text-[var(--text-muted)] border border-[var(--border-strong)] rounded px-1 py-0.5">
              {shortcut}
            </kbd>
          )}
        </span>
      </TooltipContent>
    </TooltipRoot>
  );
}
